import { useCallback, useEffect, useState } from 'react';

import { ZIP_CODE_PATTERN } from '../../shared/components/Forms/address-form.constants';
import { DeliveryCity, DeliveryCitySlot, DeliveryCityZone } from '../types/delivery-city.type';
import { UpdateDeliveryCityRequestDTO } from '../types/dtos/update-delivery-city.dto';
import { areSlotsValid, isDeliveryCityNameValid, sortSlots } from '../utils/delivery-city-form-rules.util';

type EditFormState = {
  updatedDeliveryCity: DeliveryCity;
  editedFields: UpdateDeliveryCityRequestDTO;
};

type UseDeliveryCityEditFormResult = {
  updatedDeliveryCity: DeliveryCity;
  editedFields: UpdateDeliveryCityRequestDTO;
  isEditing: boolean;
  isNameInvalid: boolean;
  isZipCodeInvalid: boolean;
  areSlotsInvalid: boolean;
  hasInvalidEditedField: boolean;
  handleNameChange: (name: string) => void;
  handleZipCodeChange: (zipCode: string) => void;
  handleZoneChange: (zone: DeliveryCityZone) => void;
  handleIsActiveChange: (isActive: boolean) => void;
  handleSlotsChange: (slots: DeliveryCitySlot[]) => void;
  handleSave: () => Promise<void>;
  resetForm: () => void;
};

const toInitialForm = (deliveryCity: DeliveryCity): EditFormState => ({
  updatedDeliveryCity: deliveryCity,
  editedFields: {},
});

const toSavedFields = ({
  name,
  slots,
  ...otherFields
}: UpdateDeliveryCityRequestDTO): UpdateDeliveryCityRequestDTO => ({
  ...otherFields,
  ...(name === undefined ? {} : { name: name.trim() }),
  ...(slots === undefined ? {} : { slots: sortSlots(slots) }),
});

export function useDeliveryCityEditForm(
  deliveryCity: DeliveryCity,
  updateDeliveryCity: (data: UpdateDeliveryCityRequestDTO) => Promise<DeliveryCity | null>
): UseDeliveryCityEditFormResult {
  const [form, setForm] = useState<EditFormState>(() => toInitialForm(deliveryCity));

  const resetForm = useCallback(() => setForm(toInitialForm(deliveryCity)), [deliveryCity]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const applyEdits = useCallback(
    (deliveryCityEdits: Partial<DeliveryCity>, requestEdits: UpdateDeliveryCityRequestDTO) =>
      setForm((previous) => ({
        updatedDeliveryCity: { ...previous.updatedDeliveryCity, ...deliveryCityEdits },
        editedFields: { ...previous.editedFields, ...requestEdits },
      })),
    []
  );

  const handleNameChange = useCallback((name: string) => applyEdits({ name }, { name }), [applyEdits]);

  const handleZipCodeChange = useCallback((zipCode: string) => applyEdits({ zipCode }, { zipCode }), [applyEdits]);

  const handleZoneChange = useCallback(
    (zone: DeliveryCityZone) => applyEdits({ zone }, { zoneId: zone.id }),
    [applyEdits]
  );

  const handleIsActiveChange = useCallback((isActive: boolean) => applyEdits({ isActive }, { isActive }), [applyEdits]);

  const handleSlotsChange = useCallback((slots: DeliveryCitySlot[]) => applyEdits({ slots }, { slots }), [applyEdits]);

  const { updatedDeliveryCity, editedFields } = form;
  const isEditing = Object.keys(editedFields).length > 0;
  const isNameInvalid = editedFields.name !== undefined && !isDeliveryCityNameValid(editedFields.name);
  const isZipCodeInvalid = editedFields.zipCode !== undefined && !ZIP_CODE_PATTERN.test(editedFields.zipCode);
  const areSlotsInvalid = editedFields.slots !== undefined && !areSlotsValid(editedFields.slots);
  const hasInvalidEditedField = isNameInvalid || isZipCodeInvalid || areSlotsInvalid;

  const handleSave = async (): Promise<void> => {
    if (!isEditing || hasInvalidEditedField) return;

    await updateDeliveryCity(toSavedFields(editedFields));
  };

  return {
    updatedDeliveryCity,
    editedFields,
    isEditing,
    isNameInvalid,
    isZipCodeInvalid,
    areSlotsInvalid,
    hasInvalidEditedField,
    handleNameChange,
    handleZipCodeChange,
    handleZoneChange,
    handleIsActiveChange,
    handleSlotsChange,
    handleSave,
    resetForm,
  };
}
